/**
 * Every string in the interface. One locale, so no i18n library — but nothing
 * inline in components either, so the game vocabulary stays consistent with
 * SPEC.md §2.
 *
 * "Ja" and "Nein" are deliberately untranslated: that is what is printed on the
 * physical ballot cards, and the app should not disagree with the box.
 */
export const ro = {
  appName: 'Secret Hitler',
  appSub: 'Sesiune de vot',

  // Roles and government
  president: 'Președinte',
  chancellor: 'Cancelar',
  proposedGovernment: 'Guvern propus',
  electedGovernment: 'Guvern în funcție',
  noGovernment: 'Niciun guvern în funcție',

  // Voting
  ja: 'Ja',
  nein: 'Nein',
  votingInProgress: 'Vot în desfășurare',
  noVoting: 'Niciun vot în desfășurare',
  waitingForVotes: 'Se așteaptă restul voturilor',
  notVotedYet: 'Nu a votat încă',
  hasVoted: 'A votat',
  yourVote: 'Votul tău',
  votesCast: (cast: number, total: number) => `${cast} din ${total} voturi`,
  governmentApproved: 'Guvern aprobat',
  governmentRejected: 'Guvern respins',

  // Tracker and chaos
  failedElections: (n: number, max: number) => `Alegeri eșuate ${n}/${max}`,
  chaosTitle: 'Țara e cufundată în haos!',
  chaosBody: 'Se promulgă legea din vârful pachetului. Limitele de mandat dispar.',

  // Warnings
  termLimited: 'Limită de mandat',
  executed: 'Executat',
  executedYou: 'Ai fost executat',
  executedBody: 'Nu mai poți vota, dar urmărești în continuare jocul.',
  kickedYou: 'Ai fost eliminat din cameră',
  cannotVote: 'Nu poți vota',
  cannotVoteAdmin: 'Ai ales să nu joci în această cameră.',

  // Joining
  chooseName: 'Alege-ți un nume',
  namePlaceholder: 'Numele tău',
  nameTaken: 'Numele este deja luat',
  nameInvalid: 'Alege un nume valid',
  photoSection: 'Poză (opțional)',
  addPhoto: 'Alege din galerie',
  takePhoto: 'Fă o poză',
  removePhoto: 'Șterge poza',
  join: 'Intră în joc',
  joining: 'Se intră…',
  scanToJoin: 'Scanează pentru a intra în joc',
  waitingToStart: 'Așteptăm începerea jocului',
  joinClosed: 'Intrările sunt închise',
  players: 'Jucători',
  noPlayersYet: 'Niciun jucător încă',

  // Admin
  adminPanel: 'Administrare',
  links: 'Linkuri',
  enterRoom: 'Intră în cameră',

  joinLinkTitle: 'Link de intrare',
  joinLinkBody: 'Dă-l jucătorilor, sau lasă-i să scaneze codul QR de pe ecran.',
  projectorLinkTitle: 'Link pentru ecranul mare',
  projectorLinkBody: 'Deschide-l pe televizor. Doar afișează, nu poate face nimic.',
  adminLinkPrivate: 'Nu îl da nimănui. Cine îl deschide preia administrarea.',
  copy: 'Copiază',
  startVote: 'Începe votul',
  annulVote: 'Anulează votul',
  setGovernment: 'Schimbă guvernul',
  execute: 'Execută',
  revive: 'Reînvie',
  kick: 'Elimină din cameră',
  rename: 'Redenumește',
  resetRoom: 'Resetează camera',
  closeRoom: 'Închide camera',
  showHistory: 'Arată istoricul',
  showLive: 'Arată jocul',
  choosePresident: 'Alege președintele',
  chooseChancellor: 'Alege cancelarul',
  adminIsPlaying: 'Joc și eu',
  adminNotPlaying: 'Doar administrez',
  createRoom: 'Creează camera',
  copied: 'Copiat',
  roomReady: 'Camera este gata',
  roomReadyBody:
    'Administrarea rămâne în acest browser. Dacă îi ștergi datele, camera se pierde.',
  confirmReset: 'Resetezi camera? Jucătorii rămân, jocul se șterge.',
  confirmClose: 'Închizi camera definitiv? Se șterge tot.',
  confirmAnnul: 'Anulezi votul? Va fi ca și cum nu s-a întâmplat.',

  // History
  history: 'Istoricul voturilor',
  historyEmpty: 'Niciun vot încheiat încă',
  electionNo: (n: number) => `Votul ${n}`,

  // Room states
  roomClosed: 'Camera a fost închisă',
  roomClosedBody: 'Mulțumim că ai jucat.',
  roomNotFound: 'Camera nu există',
  connecting: 'Se conectează…',
  reconnecting: 'Se reconectează…',

  // Errors, keyed by wire error code
  errors: {
    INVALID_PAYLOAD: 'Cerere invalidă',
    ROOM_NOT_FOUND: 'Camera nu există',
    ROOM_CLOSED: 'Camera este închisă',
    FORBIDDEN: 'Nu ai dreptul să faci asta',
    INVALID_PHASE: 'Nu se poate face acum',
    JOIN_CLOSED: 'Intrările sunt închise',
    ROOM_FULL: 'Camera este plină',
    NAME_INVALID: 'Alege un nume valid',
    NAME_TAKEN: 'Numele este deja luat',
    NOT_A_PLAYER: 'Nu ești jucător în această cameră',
    ADMIN_NOT_PLAYING: 'Ai ales să nu joci în această cameră',
    ADMIN_ALREADY_JOINED: 'Ai intrat deja în joc din alt browser',
    NOT_ELIGIBLE: 'Nu poți vota în această sesiune',
    ALREADY_VOTED: 'Ai votat deja',
    STALE_SESSION: 'Votul aparține unei sesiuni încheiate',
    PLAYER_NOT_FOUND: 'Jucătorul nu există',
    SAME_PLAYER: 'Alege două persoane diferite',
    NO_ELIGIBLE_VOTERS: 'Nu există jucători care pot vota',
    ALREADY_ALIVE: 'Jucătorul este deja în viață',
    ALREADY_DEAD: 'Jucătorul este deja executat',
    AVATAR_TOO_LARGE: 'Poza este prea mare',
    AVATAR_INVALID: 'Poza nu este valida',
  },
} as const;

export type Copy = typeof ro;
